import { createFormConfig, ValidationRules } from 'helpers/createFormConfig';
import * as Yup from 'yup';

// =============================================================================
// FIELD NAMES
// =============================================================================

export const BorrowerLoanFormFieldNames = {
  LoanAmount: 'loanAmount',
  InsuranceAmount: 'insuranceAmount',
  LoanType: 'loanType',
  InterestRateType: 'interestRateType',
  DefermentType: 'defermentType',
  InterestRatePercentage: 'interestRatePercentage',
  TotalDuration: 'totalDuration',
  DefermentPeriod: 'defermentPeriod',
  InterestRepayment: 'interestRepayment',
  RemainingDuration: 'remainingDuration',
  RentAmount: 'rentAmount',
  ResidualValue: 'residualValue',
  Tiers: 'tiers',
  TierDuration: 'duration',
  TierAmount: 'amount',
  TierRate: 'rate',
  isActive: 'isActive',
  TieredRate: 'tieredRate'
} as const;

export type BorrowerLoanFormFieldNames =
  (typeof BorrowerLoanFormFieldNames)[keyof typeof BorrowerLoanFormFieldNames];

// =============================================================================
// LOAN TYPE CODES (référentiel LoanType.code)
// =============================================================================

export const LoanTypeCodes = {
  Amortissable: 'AMORTISSABLE',
  Palier: 'PALIER',
  CreditBail: 'CREDIT-BAIL',
  Infine: 'INFINE',
  Ptz: 'PTZ'
} as const;

// =============================================================================
// TIER HELPERS
// =============================================================================

export type LoanTierValue = {
  [BorrowerLoanFormFieldNames.TierDuration]?: number | null;
  [BorrowerLoanFormFieldNames.TierAmount]?: number | null;
  [BorrowerLoanFormFieldNames.TierRate]?: number | null;
};

/** Palier vierge (durée/montant à 0, taux non renseigné). */
export const createDefaultTier = (): LoanTierValue => ({
  [BorrowerLoanFormFieldNames.TierDuration]: 0,
  [BorrowerLoanFormFieldNames.TierAmount]: 0,
  [BorrowerLoanFormFieldNames.TierRate]: null
});

/** Liste de paliers par défaut (minimum requis = 2). */
export const createDefaultTiers = (): LoanTierValue[] => [
  createDefaultTier(),
  createDefaultTier()
];

/** Somme des durées (mois) des paliers, en ignorant les valeurs vides. */
export const sumTierDurations = (tiers?: LoanTierValue[] | null): number =>
  (tiers ?? []).reduce(
    (sum, tier) =>
      sum + (Number(tier?.[BorrowerLoanFormFieldNames.TierDuration]) || 0),
    0
  );

/**
 * Vrai si tous les paliers (renseignés) partagent le même taux. Sert de source unique
 * à la normalisation au submit (taux global reporté) et à l'inférence du toggle en édition.
 */
export const areTierRatesUniform = (tiers?: LoanTierValue[] | null): boolean => {
  const rates = (tiers ?? [])
    .map((tier) => tier?.[BorrowerLoanFormFieldNames.TierRate])
    .filter((rate): rate is number => rate !== null && rate !== undefined);
  return rates.length === 0 || rates.every((rate) => rate === rates[0]);
};

// =============================================================================
// FORM VALUES TYPE
// =============================================================================

export interface BorrowerLoanFormValues {
  id?: string;
  [BorrowerLoanFormFieldNames.LoanAmount]: number;
  [BorrowerLoanFormFieldNames.isActive]?: boolean;
  [BorrowerLoanFormFieldNames.TieredRate]?: boolean;
  [BorrowerLoanFormFieldNames.InsuranceAmount]?: number;
  [BorrowerLoanFormFieldNames.LoanType]: string;
  [BorrowerLoanFormFieldNames.DefermentType]: string;
  [BorrowerLoanFormFieldNames.InterestRateType]: string;
  [BorrowerLoanFormFieldNames.InterestRatePercentage]: number;
  [BorrowerLoanFormFieldNames.TotalDuration]: number;
  [BorrowerLoanFormFieldNames.DefermentPeriod]: number;
  [BorrowerLoanFormFieldNames.RemainingDuration]?: number;
  [BorrowerLoanFormFieldNames.RentAmount]?: number;
  [BorrowerLoanFormFieldNames.ResidualValue]?: number;
  [BorrowerLoanFormFieldNames.Tiers]?: Array<{
    [BorrowerLoanFormFieldNames.TierDuration]?: number | null;
    [BorrowerLoanFormFieldNames.TierAmount]?: number | null;
    [BorrowerLoanFormFieldNames.TierRate]?: number | null;
  }>;
}

// =============================================================================
// VALIDATION RULES
// =============================================================================

export const borrowerLoanValidationRules: ValidationRules<
  Exclude<keyof BorrowerLoanFormValues, 'id'>
> = {
  [BorrowerLoanFormFieldNames.LoanAmount]: Yup.string()
    .trim()
    .required('Le montant du prêt est requis')
    .matches(/^\d+(\.\d{1,2})?$/, 'Le montant doit être un nombre valide')
    .test('min-amount', 'Le montant minimum est de 1€', (value) => {
      return value ? parseFloat(value) >= 1 : false;
    })
    .test('max-amount', 'Le montant maximum est de 10 000 000€', (value) => {
      return value ? parseFloat(value) <= 10000000 : true;
    }),

  [BorrowerLoanFormFieldNames.isActive]: Yup.boolean(),

  [BorrowerLoanFormFieldNames.TieredRate]: Yup.boolean(),

  [BorrowerLoanFormFieldNames.InsuranceAmount]: Yup.number(),

  [BorrowerLoanFormFieldNames.LoanType]: Yup.string()
    .trim()
    .required('Le type de prêt est requis'),

  [BorrowerLoanFormFieldNames.InterestRateType]: Yup.string()
    .trim()
    .required('Le type de taux est requis'),

  [BorrowerLoanFormFieldNames.DefermentType]: Yup.string()
    .trim()
    .required('Le type de taux est requis'),

  [BorrowerLoanFormFieldNames.InterestRatePercentage]: Yup.number()
    .nullable()
    .min(0, 'Le taux ne peut pas être négatif')
    .max(20, 'Le taux ne peut pas dépasser 20%')
    .test(
      'decimal-places',
      'Le taux ne peut avoir que 3 décimales maximum',
      (value) => {
        if (value === null || value === undefined) return true;
        return Number(value.toFixed(3)) === value;
      }
    )
    .typeError('Le taux doit être un nombre valide')
    // Taux global requis, sauf pour un prêt PALIER en mode "taux par palier".
    .when(
      [
        BorrowerLoanFormFieldNames.LoanType,
        BorrowerLoanFormFieldNames.TieredRate
      ],
      {
        is: (loanType: string, tieredRate: boolean) =>
          loanType === LoanTypeCodes.Palier && tieredRate === true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required("Le taux d'intérêt est requis")
      }
    ),

  [BorrowerLoanFormFieldNames.TotalDuration]: Yup.number()
    .nullable()
    .required('La durée totale est requise')
    .min(1, 'La durée minimum est de 1 mois')
    .max(600, 'La durée maximum est de 600 mois (50 ans)')
    .integer('La durée doit être un nombre entier de mois')
    .typeError('La durée doit être un nombre valide'),

  [BorrowerLoanFormFieldNames.DefermentPeriod]: Yup.number()
    .nullable()
    .required('Le différé est requis')
    .min(0, 'Le différé ne peut pas être négatif')
    .max(120, 'Le différé maximum est de 120 mois')
    .integer('Le différé doit être un nombre entier de mois')
    .typeError('Le différé doit être un nombre valide')
    .test(
      'deferment-vs-duration',
      'Le différé ne peut pas être supérieur à la durée totale',
      function (value) {
        const totalDuration =
          this.parent[BorrowerLoanFormFieldNames.TotalDuration];
        if (!value || !totalDuration) return true;
        return value <= totalDuration;
      }
    ),

  // Désormais jamais demandé sur le formulaire (Crédit-bail utilise TotalDuration
  // sous le label "Durée totale"). Laissé en optionnel pour rétrocompatibilité
  // avec d'éventuels prêts existants en base.
  [BorrowerLoanFormFieldNames.RemainingDuration]: Yup.number()
    .nullable()
    .notRequired(),

  [BorrowerLoanFormFieldNames.RentAmount]: Yup.number().when(
    BorrowerLoanFormFieldNames.LoanType,
    {
      is: LoanTypeCodes.CreditBail,
      then: (schema) =>
        schema
          .required('Le montant du loyer est requis')
          .min(1, 'Le montant du loyer minimum est de 1€')
          .max(1000000, 'Le montant du loyer maximum est de 1 000 000€')
          .test(
            'decimal-places',
            'Le montant ne peut avoir que 2 décimales maximum',
            (value) => {
              if (value === null || value === undefined) return true;
              return Number(value.toFixed(2)) === value;
            }
          )
          .typeError('Le montant du loyer doit être un nombre valide'),
      otherwise: (schema) => schema.nullable().notRequired()
    }
  ),

  // Désormais jamais demandé sur le formulaire. Laissé en optionnel pour
  // rétrocompatibilité avec d'éventuels prêts existants en base.
  [BorrowerLoanFormFieldNames.ResidualValue]: Yup.number()
    .nullable()
    .notRequired(),

  [BorrowerLoanFormFieldNames.Tiers]: Yup.array().when(
    BorrowerLoanFormFieldNames.LoanType,
    {
      is: LoanTypeCodes.Palier,
      then: (schema) =>
        schema
          .required('Les paliers sont requis')
          .min(2, 'Au moins 2 paliers sont requis')
          .of(
            Yup.object().shape({
              [BorrowerLoanFormFieldNames.TierDuration]: Yup.number()
                .required('La durée du palier est requise')
                .min(1, 'La durée du palier minimum est de 1 mois')
                .max(600, 'La durée du palier maximum est de 600 mois')
                .integer('La durée du palier doit être un nombre entier de mois')
                .typeError('La durée du palier doit être un nombre valide'),

              [BorrowerLoanFormFieldNames.TierAmount]: Yup.number()
                .required('Le montant du palier est requis')
                .min(1, 'Le montant du palier minimum est de 1€')
                .max(10000000, 'Le montant du palier maximum est de 10 000 000€')
                .test(
                  'decimal-places',
                  'Le montant ne peut avoir que 2 décimales maximum',
                  (value) => {
                    if (value === null || value === undefined) return true;
                    return Number(value.toFixed(2)) === value;
                  }
                )
                .typeError('Le montant du palier doit être un nombre valide'),

              // Taux par palier optionnel (saisi uniquement en mode "taux par palier").
              [BorrowerLoanFormFieldNames.TierRate]: Yup.number()
                .nullable()
                .notRequired()
                .min(0, 'Le taux du palier ne peut pas être négatif')
                .max(20, 'Le taux du palier ne peut pas dépasser 20%')
                .test(
                  'decimal-places',
                  'Le taux ne peut avoir que 3 décimales maximum',
                  (value) => {
                    if (value === null || value === undefined) return true;
                    return Number(value.toFixed(3)) === value;
                  }
                )
                .typeError('Le taux du palier doit être un nombre valide')
            })
          )
          .test(
            'TOTAL-TIER-DURATION',
            'La somme des durées des paliers doit être égale à la durée totale du prêt',
            function (value) {
              const totalDuration =
                this.parent[BorrowerLoanFormFieldNames.TotalDuration];
              if (!totalDuration || !value || !Array.isArray(value)) return true;

              return sumTierDurations(value) === totalDuration;
            }
          )
          .test(
            'TIER-RATE-REQUIRED',
            'Le taux est requis pour chaque palier',
            function (value) {
              const tieredRate =
                this.parent[BorrowerLoanFormFieldNames.TieredRate];
              if (!tieredRate || !Array.isArray(value)) return true;

              // Si les taux par palier sont activés, chacun doit être renseigné.
              return value.every((tier) => {
                const rate = tier?.[BorrowerLoanFormFieldNames.TierRate];
                return (
                  rate !== null &&
                  rate !== undefined &&
                  String(rate) !== '' &&
                  !Number.isNaN(Number(rate))
                );
              });
            }
          ),
      otherwise: (schema) => schema.nullable().notRequired()
    }
  )
};

// =============================================================================
// FORM CONFIG
// =============================================================================

export const borrowerLoanFormConfig = createFormConfig<BorrowerLoanFormValues>(
  borrowerLoanValidationRules as ValidationRules<keyof BorrowerLoanFormValues>,
  {
    [BorrowerLoanFormFieldNames.LoanType]: LoanTypeCodes.Amortissable,
    [BorrowerLoanFormFieldNames.isActive]: true,
    [BorrowerLoanFormFieldNames.TieredRate]: false,
    [BorrowerLoanFormFieldNames.InterestRateType]: 'fixe',
    [BorrowerLoanFormFieldNames.DefermentType]: 'aucun-differe',
    [BorrowerLoanFormFieldNames.DefermentPeriod]: 0,
    [BorrowerLoanFormFieldNames.TotalDuration]: 1,
    [BorrowerLoanFormFieldNames.Tiers]: createDefaultTiers()
  }
);
